import { call, put, select, take, race, takeLatest, takeEvery } from 'redux-saga/effects'
import type { PayloadAction } from '@reduxjs/toolkit'
import { collection, query, where, orderBy, limit } from 'firebase/firestore'
import { createSnapshotChannel } from '../utils/createSnapshotChannel'
import { db } from '@/lib/firebase'
import {
    subscribeTaskActivitySaga,
    unsubscribeTaskActivitySaga,
    subscribeProjectActivitySaga,
    unsubscribeProjectActivitySaga,
    loadMoreProjectActivitySaga,
    subscribeWorkspaceActivitySaga,
    unsubscribeWorkspaceActivitySaga,
    loadMoreWorkspaceActivitySaga,
    setTaskActivity,
    setTaskActivityLoading,
    setProjectActivity,
    incrementProjectActivityLimit,
    setProjectActivityLoading,
    setWorkspaceActivity,
    incrementWorkspaceActivityLimit,
    setWorkspaceActivityLoading,
    ACTIVITY_PAGE_SIZE,
    type ActivityEvent,
} from './activitySlice'
import type { RootState } from '../index'

const MAX_TASK_SUBSCRIPTIONS = 20

// LRU set: Map preserves insertion order; oldest entry = first key
class LRUSet {
    private map = new Map<string, true>()

    has(id: string): boolean { return this.map.has(id) }

    // Returns evicted id if at capacity, null otherwise
    add(id: string): string | null {
        if (this.map.has(id)) { this.map.delete(id); this.map.set(id, true); return null }
        let evicted: string | null = null
        if (this.map.size >= MAX_TASK_SUBSCRIPTIONS) {
            evicted = this.map.keys().next().value ?? null
            if (evicted) this.map.delete(evicted)
        }
        this.map.set(id, true)
        return evicted
    }

    delete(id: string): void { this.map.delete(id) }
}

const activeTaskSubscriptions = new LRUSet()

function* subscribeTaskActivityWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const taskId = action.payload

    if (activeTaskSubscriptions.has(taskId)) return
    const evicted: string | null = activeTaskSubscriptions.add(taskId)
    if (evicted) yield put(unsubscribeTaskActivitySaga(evicted))

    const task = yield select((state: RootState) => state.tasks.byId[taskId])
    if (!task) { activeTaskSubscriptions.delete(taskId); return }

    const q = query(
        collection(db, 'activity'),
        where('taskId', '==', taskId),
        where('workspaceId', '==', task.workspaceId),
        where('projectId', '==', task.projectId),
        orderBy('createdAt', 'asc'),
        limit(100)
    )

    const channel: ReturnType<typeof createSnapshotChannel> = yield call(createSnapshotChannel, q)
    yield put(setTaskActivityLoading({ taskId, loading: true }))

    try {
        while (true) {
            const result: any = yield race({
                event: take(channel),
                stop: take((a: any) => a.type === unsubscribeTaskActivitySaga.type && a.payload === taskId),
            })
            if (result.stop) return
            if (result.event?.type === 'error') {
                console.error('[ActivitySaga] Task activity error:', result.event.error)
                return
            }
            if (result.event?.type === 'data') {
                const events: ActivityEvent[] = result.event.snapshot.docs.map((d: any) => ({ id: d.id, ...d.data() }))
                yield put(setTaskActivity({ taskId, events }))
                yield put(setTaskActivityLoading({ taskId, loading: false }))
            }
        }
    } finally {
        channel.close()
        activeTaskSubscriptions.delete(taskId)
        yield put(setTaskActivityLoading({ taskId, loading: false }))
    }
}

function* subscribeProjectActivityWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const projectId = action.payload

    while (true) {
        const project = yield select((state: RootState) => state.projects.byId[projectId])
        if (!project) return

        const currentLimit: number = yield select(
            (state: RootState) => state.activity.byProjectId[projectId]?.limit ?? ACTIVITY_PAGE_SIZE
        )

        const q = query(
            collection(db, 'activity'),
            where('projectId', '==', projectId),
            where('workspaceId', '==', project.workspaceId),
            orderBy('createdAt', 'desc'),
            limit(currentLimit)
        )

        const channel: ReturnType<typeof createSnapshotChannel> = yield call(createSnapshotChannel, q)
        yield put(setProjectActivityLoading({ projectId, loading: true }))

        let shouldResubscribe = false

        try {
            while (true) {
                const result: any = yield race({
                    event: take(channel),
                    stop: take((a: any) => a.type === unsubscribeProjectActivitySaga.type && a.payload === projectId),
                    loadMore: take((a: any) => a.type === loadMoreProjectActivitySaga.type && a.payload === projectId),
                })

                if (result.stop) return

                if (result.loadMore) {
                    yield put(incrementProjectActivityLimit(projectId))
                    shouldResubscribe = true
                    break
                }

                if (result.event?.type === 'error') {
                    console.error('[ActivitySaga] Project activity error:', result.event.error)
                    return
                }
                if (result.event?.type === 'data') {
                    const events: ActivityEvent[] = result.event.snapshot.docs.map((d: any) => ({ id: d.id, ...d.data() }))
                    const hasMore = result.event.snapshot.size === currentLimit
                    yield put(setProjectActivity({ projectId, events, hasMore }))
                    yield put(setProjectActivityLoading({ projectId, loading: false }))
                }
            }
        } finally {
            channel.close()
            yield put(setProjectActivityLoading({ projectId, loading: false }))
        }

        if (!shouldResubscribe) return
    }
}

function* subscribeWorkspaceActivityWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const workspaceId = action.payload

    while (true) {
        const currentLimit: number = yield select(
            (state: RootState) => state.activity.workspaceLimit
        )

        const q = query(
            collection(db, 'activity'),
            where('workspaceId', '==', workspaceId),
            orderBy('createdAt', 'desc'),
            limit(currentLimit)
        )

        const channel: ReturnType<typeof createSnapshotChannel> = yield call(createSnapshotChannel, q)
        yield put(setWorkspaceActivityLoading(true))

        let shouldResubscribe = false

        try {
            while (true) {
                const result: any = yield race({
                    event: take(channel),
                    stop: take(unsubscribeWorkspaceActivitySaga),
                    loadMore: take(loadMoreWorkspaceActivitySaga),
                })

                if (result.stop) return

                if (result.loadMore) {
                    yield put(incrementWorkspaceActivityLimit())
                    shouldResubscribe = true
                    break
                }

                if (result.event?.type === 'error') {
                    console.error('[ActivitySaga] Workspace activity error:', result.event.error)
                    return
                }
                if (result.event?.type === 'data') {
                    const events: ActivityEvent[] = result.event.snapshot.docs.map((d: any) => ({ id: d.id, ...d.data() }))
                    const hasMore = result.event.snapshot.size === currentLimit
                    yield put(setWorkspaceActivity({ events, hasMore }))
                    yield put(setWorkspaceActivityLoading(false))
                }
            }
        } finally {
            channel.close()
            yield put(setWorkspaceActivityLoading(false))
        }

        if (!shouldResubscribe) return
    }
}

export function* watchActivitySaga() {
    yield takeEvery(subscribeTaskActivitySaga.type, subscribeTaskActivityWorker)
    yield takeLatest(subscribeProjectActivitySaga.type, subscribeProjectActivityWorker)
    yield takeLatest(subscribeWorkspaceActivitySaga.type, subscribeWorkspaceActivityWorker)
}
